import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RbacAdminService {

  private baseUrl = environment.BASE_LA + '/api/admin/rbac';

  constructor(private http: HttpClient) { }

  getRoles(): Promise<any> {
    return this.http.get(`${this.baseUrl}/roles`).toPromise();
  }

  getUsers(params: { page?: number; limit?: number; search?: string; status?: string; role?: string; department?: string; level?: string } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.search) queryParams.set('search', params.search);
    if (params.status) queryParams.set('status', params.status);
    if (params.role) queryParams.set('role', params.role);
    if (params.department) queryParams.set('department', params.department);
    if (params.level) queryParams.set('level', params.level);
    return this.http.get(`${this.baseUrl}/users?${queryParams.toString()}`).toPromise();
  }

  assignRole(userId: string, roleCode: string): Promise<any> {
    return this.http.post(`${this.baseUrl}/assign-role`, { userId, roleCode }).toPromise();
  }

  removeRole(userId: string, roleCode?: string): Promise<any> {
    const body: any = { userId };
    if (roleCode) body.roleCode = roleCode;
    return this.http.post(`${this.baseUrl}/remove-role`, body).toPromise();
  }

  getRolePermissions(roleId: string): Promise<any> {
    return this.http.get(`${this.baseUrl}/role/${roleId}/permissions`).toPromise();
  }

  getUserPermissions(userId: string): Promise<any> {
    return this.http.get(`${this.baseUrl}/users/${userId}/permissions`).toPromise();
  }

  /** Catálogo de permisos que se asignan por usuario y a quién está asignado cada uno. */
  getIndividualPermissions(): Promise<any> {
    return this.http.get(`${this.baseUrl}/individual-permissions`).toPromise();
  }

  setUserIndividualPermission(userId: string, code: string, isGranted: boolean): Promise<any> {
    return this.http.put(`${this.baseUrl}/users/${userId}/individual-permissions`, { code, isGranted }).toPromise();
  }

  createRole(roleData: {
    code: string;
    name: string;
    description: string;
    department: string;
    level: number;
    permissions?: { module: string; submodule: string; actions: string[] }[];
  }): Promise<any> {
    return this.http.post(`${this.baseUrl}/roles`, roleData).toPromise();
  }

  deleteRole(roleCode: string): Promise<any> {
    return this.http.delete(`${this.baseUrl}/roles/${roleCode}`).toPromise();
  }

  getRolePermissionsByCode(roleCode: string): Promise<any> {
    return this.http.get(`${this.baseUrl}/roles/${roleCode}/permissions-matrix`).toPromise();
  }

  getMatriz(): Promise<any> {
    return this.http.get(`${this.baseUrl}/matriz`).toPromise();
  }

  // ── Agregar permisos desde la pantalla (pestaña «Agregar permisos») ──

  /** Pantallas (módulos) de la matriz, con sus grupos y las filas que ya muestran, para elegir el lugar del submódulo nuevo. */
  getPantallas(): Promise<any> {
    return this.http.get(`${this.baseUrl}/pantallas`).toPromise();
  }

  /** Rutas del servidor (método y ruta) que se pueden proteger junto con un botón. */
  getRutasServidor(): Promise<any> {
    return this.http.get(`${this.baseUrl}/rutas-servidor`).toPromise();
  }

  /** Crea de una vez los permisos, las marcas y las protecciones de una pantalla. 409 = código repetido y no se crea nada. */
  agregarPermisos(cuerpo: any): Promise<any> {
    return this.http.post(`${this.baseUrl}/agregar-permisos`, cuerpo).toPromise();
  }

  /**
   * Roles activos y si hoy ven una pantalla que exige esos permisos: [{ code, name, department, ven, yaTieneTodo? }].
   * Con `codigos` vacío (la pantalla no exige permisos) todos la ven. Sirve para dar los permisos nuevos a esos roles al agregarlos.
   */
  getRolesQueVen(codigos: string[]): Promise<any> {
    const lista = (codigos || []).map((c) => encodeURIComponent(c)).join(',');
    return this.http.get(`${this.baseUrl}/roles-que-ven?codigos=${lista}`).toPromise();
  }

  /** Marcas activas (lo que ya está protegido en pantalla). */
  getMarcas(): Promise<any> {
    return this.http.get(`${this.baseUrl}/marcas`).toPromise();
  }

  /** Apaga una marca; el permiso sigue en la matriz. */
  quitarMarca(id: string): Promise<any> {
    return this.http.delete(`${this.baseUrl}/marcas/${encodeURIComponent(id)}`).toPromise();
  }

  /** Llamadas del servidor que cuidan los permisos creados desde la pantalla: { _id, metodo, ruta, permiso, activo }. Nacen apagadas. */
  getProtecciones(): Promise<any> {
    return this.http.get(`${this.baseUrl}/protecciones`).toPromise();
  }

  /** Enciende (`true`) o apaga (`false`) una de esas protecciones. 400 = datos no válidos, 404 = ya no existe. */
  cambiarProteccion(id: string, activo: boolean): Promise<any> {
    return this.http.put(`${this.baseUrl}/protecciones/${encodeURIComponent(id)}`, { activo }).toPromise();
  }


  // Fase 5 · Organizar la matriz
  // Todo toca solo presentación (nombres y orden); los códigos de los permisos no cambian. 409 = nombre repetido.

  /** Cambia una fila entera: nombre, módulo, grupo o lugar. `despuesDe` = nombre de fila en el destino; null = al final; `alInicio: true` = de primera (no va con despuesDe). */
  editarFila(module: string, submodule: string, cambios: { fila?: string; pantalla?: string; grupo?: string; despuesDe?: string | null; alInicio?: boolean }): Promise<any> {
    return this.http.put(`${this.baseUrl}/fila`, { module, submodule, cambios }).toPromise();
  }

  /** Renombra una casilla o la mueve un lugar dentro de su fila. */
  editarCasilla(code: string, cambios: { casilla?: string; mover?: 'antes' | 'despues' }): Promise<any> {
    return this.http.put(`${this.baseUrl}/casilla`, { code, cambios }).toPromise();
  }

  /** Elimina una fila creada desde la interfaz (con sus casillas). 400 = alguna viene del sistema. Devuelve los conteos de lo borrado. */
  eliminarFila(module: string, submodule: string): Promise<any> {
    return this.http.delete(`${this.baseUrl}/fila`, { body: { module, submodule } }).toPromise();
  }

  /** Elimina una casilla creada desde la interfaz. 400 = viene del sistema. */
  eliminarCasilla(code: string): Promise<any> {
    return this.http.delete(`${this.baseUrl}/casilla`, { body: { code } }).toPromise();
  }

  // (crearPantalla ya lo agregó «Agregar permisos»: se reúsa.)

  /** Renombra un módulo, le cambia el icono o lo cambia de lugar (`despuesDe` = id del módulo anterior; null = al final). */
  editarPantalla(id: string, cambios: { nombre?: string; icono?: string; despuesDe?: string | null; alInicio?: boolean }): Promise<any> {
    return this.http.put(`${this.baseUrl}/pantallas/${encodeURIComponent(id)}`, cambios).toPromise();
  }

  /** Grupo nuevo dentro de un módulo. */
  crearGrupo(pantallaId: string, cuerpo: { nombre: string; icono?: string }): Promise<any> {
    return this.http.post(`${this.baseUrl}/pantallas/${encodeURIComponent(pantallaId)}/grupos`, cuerpo).toPromise();
  }

  /** Elimina un grupo creado desde la interfaz y vacío. 409 = tiene filas o viene del sistema (el mensaje del servidor lo explica). */
  eliminarGrupo(pantallaId: string, grupoId: string): Promise<any> {
    return this.http.delete(`${this.baseUrl}/pantallas/${encodeURIComponent(pantallaId)}/grupos/${encodeURIComponent(grupoId)}`).toPromise();
  }

  /** Elimina un módulo creado desde la interfaz y vacío. 409 = tiene filas o viene del sistema. */
  eliminarPantalla(id: string): Promise<any> {
    return this.http.delete(`${this.baseUrl}/pantallas/${encodeURIComponent(id)}`).toPromise();
  }

  /** `visibles`: todos los códigos que la matriz muestra; el núcleo sólo cambia esos (RBAC-03). */
  updateRolePermissions(roleCode: string, permissions: { module: string; submodule: string; actions: string[] }[], visibles: string[]): Promise<any> {
    return this.http.put(`${this.baseUrl}/roles/${roleCode}/permissions`, { permissions, visibles }).toPromise();
  }

  getAllDepartments(): Promise<any> {
    return this.http.get(`${this.baseUrl}/departments/all`).toPromise();
  }

  createDepartment(name: string): Promise<any> {
    return this.http.post(`${this.baseUrl}/departments`, { name }).toPromise();
  }

  deleteDepartment(name: string): Promise<any> {
    return this.http.delete(`${this.baseUrl}/departments/${encodeURIComponent(name)}`).toPromise();
  }

  reassignDepartment(sourceDepartment: string, targetDepartment: string): Promise<any> {
    return this.http.post(`${this.baseUrl}/departments/reassign`, { sourceDepartment, targetDepartment }).toPromise();
  }

  getRoleUsers(roleCode: string): Promise<any> {
    return this.http.get(`${this.baseUrl}/roles/${roleCode}/users`).toPromise();
  }

  updateRoleDepartment(roleCode: string, department: string | null): Promise<any> {
    return this.http.put(`${this.baseUrl}/roles/${roleCode}/department`, { department }).toPromise();
  }

  getAuditLogs(params: { page?: number; limit?: number; action?: string; entity?: string; search?: string; dateFrom?: string; dateTo?: string } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.action && params.action !== 'all') queryParams.set('action', params.action);
    if (params.entity && params.entity !== 'all') queryParams.set('entity', params.entity);
    if (params.search) queryParams.set('search', params.search);
    if (params.dateFrom) queryParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.set('dateTo', params.dateTo);
    return this.http.get(`${this.baseUrl}/audit-logs?${queryParams.toString()}`).toPromise();
  }

  // Fase 5 · Agregar permisos

  /** Módulo nuevo de la matriz: { nombre, icono?, despuesDe?: <id del módulo anterior> | null }. 409 = el nombre ya existe. */
  crearPantalla(datos: { nombre: string; icono?: string; despuesDe?: string | null }): Promise<any> {
    return this.http.post(`${this.baseUrl}/pantallas`, datos).toPromise();
  }
}
